import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const ACCENT = '#10B981';
const CYAN = '#06B6D4';

const faqData = [
  {
    id: 'gs1',
    category: 'Getting Started',
    q: 'What is ODC and how does it work?',
    a: 'ODC (OnlineDigitalCares) is a fully autonomous AI marketing platform. Complete onboarding once, and ODC AI agents start running key workflows automatically: social media publishing, WhatsApp outreach, lead follow-ups, and SEO execution.',
  },
  {
    id: 'gs2',
    category: 'Getting Started',
    q: 'How long does setup take?',
    a: 'Most teams complete setup in about 30 minutes. The guided onboarding captures your business profile, brand voice, social channels, and operating goals so automation can begin immediately.',
  },
  {
    id: 'gs3',
    category: 'Getting Started',
    q: 'Do I need technical expertise?',
    a: 'No technical expertise is required. ODC is designed for business teams and founders, with step-by-step setup and no coding needed.',
  },
  {
    id: 'ag1',
    category: 'AI Agents',
    q: 'What do ODC AI agents handle?',
    a: 'Agents are organized by function: lead generation, engagement, content, paid media, SEO, automation, and operations. Each agent performs a focused task and contributes to your overall growth pipeline.',
  },
  {
    id: 'ag2',
    category: 'AI Agents',
    q: 'How do I enable or disable agents?',
    a: 'Open the Agents page or Dashboard and use the toggle next to each agent. Changes apply instantly, so you can control your stack in real time.',
  },
  {
    id: 'ag3',
    category: 'AI Agents',
    q: 'What does the GEO Guardian agent do?',
    a: 'GEO Guardian improves your AI-search visibility by optimizing content for generative search experiences and citation opportunities.',
  },
  {
    id: 'pr1',
    category: 'Pricing & Plans',
    q: 'What plans are available?',
    a: 'ODC offers Starter, Growth, Pro, Enterprise, and Agency plans. Each tier includes different automation depth, integrations, and support capacity.',
  },
  {
    id: 'pr2',
    category: 'Pricing & Plans',
    q: 'Can I upgrade or downgrade anytime?',
    a: 'Yes. You can switch plans from Settings → Subscription. Upgrades apply immediately, while downgrades typically apply at the next billing cycle.',
  },
  {
    id: 'ca1',
    category: 'AI Calling',
    q: 'How does AI calling work?',
    a: 'ODC AI Calling automates lead outreach during configured business hours using your approved voice profile and conversation logic.',
  },
  {
    id: 'ws1',
    category: 'WhatsApp & Social',
    q: 'How do I set up WhatsApp integration?',
    a: 'Connect your WhatsApp Business API credentials in Settings → Integrations. Once connected, you can automate campaigns, flows, and broadcast messaging.',
  },
  {
    id: 'se1',
    category: 'GEO & SEO',
    q: 'What is GEO Score?',
    a: 'GEO Score measures your visibility in AI-assisted discovery channels and generative search surfaces. Higher scores indicate stronger citation and discoverability performance.',
  },
  {
    id: 'wb1',
    category: 'WebBuilder & App Builder',
    q: 'What does AI WebBuilder include?',
    a: 'AI WebBuilder can generate a structured, responsive website with core pages, optimized content, and SEO-ready architecture based on your business profile.',
  },
  {
    id: 'bi1',
    category: 'Billing & Payments',
    q: 'Which payment methods are supported?',
    a: 'ODC supports major payment methods including UPI, cards, and net banking, along with monthly and annual billing options.',
  },
  {
    id: 'sp1',
    category: 'Security & Privacy',
    q: 'Is my data secure?',
    a: 'Yes. ODC uses secure authentication and encrypted data handling aligned with modern application security standards and compliance expectations.',
  },
  {
    id: 'tr1',
    category: 'Troubleshooting',
    q: 'Dashboard is not loading. What should I do?',
    a: 'Refresh the page, clear browser cache, and sign in again. If the issue persists, create a support ticket with screenshots and your browser details.',
  },
] as const;

const CATEGORIES = [
  'All',
  'Getting Started',
  'AI Agents',
  'Pricing & Plans',
  'AI Calling',
  'WhatsApp & Social',
  'GEO & SEO',
  'WebBuilder & App Builder',
  'Billing & Payments',
  'Security & Privacy',
  'Troubleshooting',
] as const;

export function FaqPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<(typeof CATEGORIES)[number]>('All');

  const filteredFaqs = useMemo(() => {
    return faqData.filter((faq) => {
      const matchCat = activeCategory === 'All' || faq.category === activeCategory;
      const q = searchQuery.trim().toLowerCase();
      const matchSearch =
        !q || faq.q.toLowerCase().includes(q) || faq.a.toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [activeCategory, searchQuery]);

  const groupMode = activeCategory === 'All' && !searchQuery.trim();

  const pill = (on: boolean): React.CSSProperties => ({
    border: `1px solid ${on ? ACCENT : 'var(--border)'}`,
    borderRadius: 999,
    padding: '8px 20px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    background: on ? ACCENT : 'var(--card)',
    color: on ? '#fff' : 'var(--text)',
  });

  const renderAccordionItem = (faq: (typeof faqData)[number]) => {
    const open = openId === faq.id;
    return (
      <div
        key={faq.id}
        style={{
          border: `1px solid ${open ? 'rgba(16,185,129,0.3)' : 'var(--border)'}`,
          borderRadius: 16,
          marginBottom: 8,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
        }}
      >
        <button
          type="button"
          onClick={() => setOpenId(open ? null : faq.id)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            padding: '20px 24px',
            cursor: 'pointer',
            background: open ? 'rgba(16,185,129,0.04)' : 'var(--card)',
            border: 'none',
            textAlign: 'left',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)', paddingRight: 16 }}>{faq.q}</span>
          <span
            style={{
              color: ACCENT,
              fontSize: 12,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
              flexShrink: 0,
            }}
          >
            ▼
          </span>
        </button>
        {open ? (
          <div
            style={{
              padding: '0 24px 20px',
              fontSize: 14,
              lineHeight: 1.7,
              color: 'var(--muted)',
              borderTop: '1px solid var(--border)',
              paddingTop: 16,
            }}
          >
            {faq.a}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="odc-page relative z-10 min-h-screen" style={{ padding: '24px 6%', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 8 }}>
        <Link to="/" style={{ fontSize: 13, color: 'var(--muted)', textDecoration: 'none' }}>
          ← Home
        </Link>
      </div>

      <div style={{ marginBottom: 28 }}>
        <span
          style={{
            display: 'inline-flex',
            padding: '5px 14px',
            borderRadius: 999,
            border: '1px solid rgba(6,182,212,0.35)',
            background: 'rgba(6,182,212,0.1)',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.14em',
            color: CYAN,
            textTransform: 'uppercase',
          }}
        >
          HELP CENTER
        </span>
        <h1 style={{ margin: '14px 0 0', fontSize: 'clamp(1.75rem, 4vw, 2.35rem)', fontWeight: 800, color: 'var(--text)' }}>
          Frequently Asked Questions
        </h1>
        <p style={{ margin: '12px 0 0', color: 'var(--muted)', fontSize: 15, lineHeight: 1.6 }}>
          Get clear answers to everything you need to know about ODC
        </p>
      </div>

      <div className="odc-card" style={{ marginBottom: 20, padding: 20, borderRadius: 20, border: '1px solid var(--border)', background: 'var(--card)' }}>
        <input
          className="odc-input"
          style={{ width: '100%', fontSize: 16, padding: '14px 20px' }}
          placeholder="Search questions... e.g. 'pricing', 'voice clone', 'agents'"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 32, justifyContent: 'center', flexWrap: 'wrap', marginTop: 18 }}>
          <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>📚 {faqData.length}+ Questions</span>
          <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>🔍 {CATEGORIES.length - 1} Categories</span>
          <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>⚡ Instant Answers</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
        {CATEGORIES.map((cat) => (
          <button key={cat} type="button" onClick={() => setActiveCategory(cat)} style={pill(activeCategory === cat)}>
            {cat}
          </button>
        ))}
      </div>

      {searchQuery.trim() ? (
        <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 16 }}>
          {filteredFaqs.length} results for &apos;{searchQuery}&apos;
        </p>
      ) : null}

      {filteredFaqs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: 56 }}>🔍</div>
          <h2 style={{ margin: '16px 0 0', fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>No results found</h2>
          <p style={{ color: 'var(--muted)', fontSize: 14, marginTop: 8 }}>
            Create a support ticket and our team will assist you personally
          </p>
          <Link to="/support" className="odc-btn-primary" style={{ marginTop: 20, display: 'inline-flex', textDecoration: 'none' }}>
            Raise Support Ticket
          </Link>
        </div>
      ) : groupMode ? (
        CATEGORIES.filter((c) => c !== 'All').map((cat, idx) => {
          const items = filteredFaqs.filter((f) => f.category === cat);
          if (items.length === 0) return null;
          return (
            <div key={cat} style={{ marginBottom: 8 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: ACCENT,
                  marginBottom: 12,
                  marginTop: idx === 0 ? 0 : 32,
                }}
              >
                {cat}
              </div>
              {items.map((faq) => renderAccordionItem(faq))}
            </div>
          );
        })
      ) : (
        <div>{filteredFaqs.map((faq) => renderAccordionItem(faq))}</div>
      )}

      <div
        className="odc-card"
        style={{
          marginTop: 48,
          padding: 32,
          borderRadius: 20,
          border: '1px solid var(--border)',
          background: 'var(--card)',
          textAlign: 'center',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>Still need help? We are here for you!</h2>
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap', marginTop: 24 }}>
          {[
            {
              icon: '💬',
              title: 'Support Ticket',
              body: '48 hour response guarantee',
              btn: (
                <Link to="/support" className="odc-btn-primary" style={{ marginTop: 12, display: 'inline-flex', textDecoration: 'none' }}>
                  Raise Ticket
                </Link>
              ),
            },
            {
              icon: '📧',
              title: 'Email Support',
              body: 'support@onlinedigitalcares.com',
              btn: (
                <a href="mailto:support@onlinedigitalcares.com" className="odc-btn-ghost" style={{ marginTop: 12, display: 'inline-flex', textDecoration: 'none' }}>
                  Send Email
                </a>
              ),
            },
            {
              icon: '📱',
              title: 'WhatsApp Support',
              body: 'Mon-Sat, 9AM-6PM IST',
              btn: (
                <a
                  href="https://wa.me/919634405161"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="odc-btn-ghost"
                  style={{ marginTop: 12, display: 'inline-flex', textDecoration: 'none' }}
                >
                  Chat on WhatsApp
                </a>
              ),
            },
          ].map((c) => (
            <div
              key={c.title}
              style={{
                flex: '1 1 200px',
                maxWidth: 260,
                border: '1px solid var(--border)',
                borderRadius: 16,
                padding: 20,
                background: 'var(--bg2)',
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: 'rgba(16,185,129,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 28,
                  margin: '0 auto',
                }}
              >
                {c.icon}
              </div>
              <h3 style={{ margin: '12px 0 0', fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{c.title}</h3>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--muted)' }}>{c.body}</p>
              {c.btn}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
